import classNames from "classnames";
import { subDays, subMonths, subWeeks, subYears } from "date-fns";

import { APIFilter, getFilterQuery } from "shared/api/utils";
import { cloneObject, randomID } from "shared/utils";

import {
  ADVANCED_FILTERS_WIDTH_CLASS,
  ALL_FILTER_SEPARATOR,
  ANY_FILTER_SEPARATOR,
  DEFAULT_FILTER_BUILDER_STATE,
  QUICK_FILTERS_WIDTH_CLASS,
  SIMPLIFY_FILTER_IGNORED_OPERATORS,
  SIMPLIFY_FILTER_MAPPING,
} from "features/ui/Filters/FilterBuilder/constants";
import { AnyAll } from "features/ui/Filters/FilterBuilder/FilterGroup/AnyAllSelect";
import {
  FilterGroupState,
  FilterRowState,
  FilterRowStateNotNull,
} from "features/ui/Filters/FilterBuilder/types";
import {
  EMPTY_FILTER_VALUE,
  FilterOperator,
  FilterState,
} from "features/ui/Filters/types";
import {
  areFilterValuesComplete,
  getOperatorFromAPIOperator,
  isFilterStateCorrect,
  isValidDate,
} from "features/ui/Filters/utils";
import { SelectOption } from "features/ui/Select";

import { filterBuilderQueryToFilterBuilderState } from "./filterParsing";
import {
  getFilterGroupStateTopLevelRowAttributes,
  getTopLevelRowFromFilterGroupState,
  isDefaultAdvancedFilterState,
} from "./filterState";
import { isFilterBuilderStateValid } from "./filterValidation";

/**
 * Processes filter rows containing EMPTY_FILTER_VALUE placeholder values.
 * Transforms them into IS_EMPTY operators (or OR groups if mixed with other values).
 */
const processEmptyFilterValues = (
  groupState: FilterGroupState
): FilterGroupState => ({
  ...groupState,
  children: groupState.children.map((child) => {
    if (child.type === "group") {
      return processEmptyFilterValues(child as FilterGroupState);
    }

    const row = child as FilterRowState;
    const values = row.values || [];
    const hasEmpty = values.includes(EMPTY_FILTER_VALUE);

    if (!hasEmpty) {
      return child;
    }

    const nonEmptyValues = values.filter((v) => v !== EMPTY_FILTER_VALUE);

    // Case 1: Only EMPTY_FILTER_VALUE → convert to IS_EMPTY
    if (nonEmptyValues.length === 0) {
      return {
        ...row,
        operator: FilterOperator.IS_EMPTY,
        values: ["null"],
      } as FilterRowState;
    }

    // Case 2: EMPTY_FILTER_VALUE + other values → create OR group
    return {
      type: "group",
      id: `${row.id}-or-group`,
      anyAll: "any",
      children: [
        { ...row, values: nonEmptyValues },
        {
          type: "row",
          id: `${row.id}-empty`,
          attribute: row.attribute,
          operator: FilterOperator.IS_EMPTY,
          values: ["null"],
        } as FilterRowState,
      ],
    } as FilterGroupState;
  }),
});

// The DSL quotes each value, with no escape, so a value carrying a double quote cannot be written into it. The
// quotes are dropped rather than the value refused. Anything saved off a filter has to be sanitized the same way,
// or it would import rows the preview never showed.
export const sanitizeFilterValue = (value: unknown): string =>
  value ? value.toString().replace(/"/g, "") : "";

const isIncompleteRange = ({ operator, values }: FilterRowState): boolean =>
  operator === FilterOperator.BETWEEN &&
  !areFilterValuesComplete(operator, values ?? []);

// The state with its incomplete ranges taken out. The Filters dropdown (features/ui/Filters/Filters.tsx) applies as
// it is edited, so a range reaches the query and the filter count with one bound while the other is being picked,
// and a quick filter commits a range with no bound yet. Such a row is taken out as not filtered yet, rather than
// failing the validity check and voiding the filters beside it — the static filters included. A group that held
// nothing but such ranges goes with them; one that was empty to begin with is left as it was.
export const withoutIncompleteRanges = (
  groupState: FilterGroupState
): FilterGroupState => {
  // A state read back from storage can be missing its children; isFilterBuilderStateValid guards the same way.
  if (!groupState.children) return groupState;

  return {
    ...groupState,
    children: groupState.children.flatMap(
      (child): FilterGroupState["children"] => {
        if (child.type === "row") {
          return isIncompleteRange(child) ? [] : [child];
        }

        if (!child.children) return [child];

        const settled = withoutIncompleteRanges(child);

        return child.children.length > 0 && settled.children.length === 0
          ? []
          : [settled];
      }
    ),
  };
};

/**
 * Converts a FilterBuilder state to a query string (API filter format).
 * - A "between" row missing a bound is left out before the state is validated, so the rows beside it still serialize.
 * - Returns "" if the state is empty or invalid. Note that default state is also invalid.
 * - The reverse of this is filterBuilderQueryToFilterBuilderState.
 */
export const getFiltersQuery = (
  groupState?: FilterGroupState,
  staticFilters?: APIFilter[]
): string => {
  if (!groupState && !staticFilters) return "";

  const settledGroupState = groupState && withoutIncompleteRanges(groupState);

  if (
    settledGroupState &&
    !isFilterBuilderStateValid(settledGroupState) &&
    !isDefaultAdvancedFilterState(settledGroupState)
  ) {
    return "";
  }

  // Process EMPTY_FILTER_VALUE placeholder values before converting to query
  const processedGroupState = settledGroupState
    ? processEmptyFilterValues(settledGroupState)
    : settledGroupState;

  const transformToQuery = (
    groupState: FilterGroupState<FilterRowStateNotNull>
  ): string => {
    const { anyAll, children } = groupState;

    return children
      .map((child) => {
        if (child.type === "group") {
          return `(${transformToQuery(child as typeof groupState)})`;
        }

        const { values } = child as FilterRowStateNotNull;

        // skip attributes that have an empty values array
        if (!values.length) return null;

        const sanitizedChild = {
          ...child,
          values: values.map(sanitizeFilterValue),
        };

        return getFilterQuery(sanitizedChild);
      })
      .filter(Boolean)
      .join(anyAll === "any" ? ANY_FILTER_SEPARATOR : ALL_FILTER_SEPARATOR);
  };

  let filterQuery =
    (processedGroupState &&
      transformToQuery(
        processedGroupState as FilterGroupState<FilterRowStateNotNull>
      )) ||
    "";

  // If we have staticFilters we need to make sure this is included in the query via top-level commas (ALL)
  let staticFiltersQuery = "";
  if (staticFilters && staticFilters.length > 0) {
    staticFiltersQuery = staticFilters
      .reduce((acc, { name, op, value }) => {
        const filterQuery = getFilterQuery({
          id: name,
          type: "row",
          attribute: name,
          operator: getOperatorFromAPIOperator(op),
          values: value as string[],
        });
        if (filterQuery) {
          acc.push(filterQuery);
        }

        return acc;
      }, [] as string[])
      .join(ALL_FILTER_SEPARATOR);
  }

  // In the case of top-level group having "any" as a separator, we have to add another group so that static filters
  // will be ANDed with the rest of the filters (otherwise we'd introduce mixed delimiters, which is not a valid FilterGroupState!)
  if (staticFiltersQuery) {
    if (
      settledGroupState?.anyAll === "any" &&
      settledGroupState?.children?.length > 1
    ) {
      filterQuery = `(${filterQuery})`;
    }

    filterQuery += (filterQuery && ALL_FILTER_SEPARATOR) + staticFiltersQuery;
  }

  return filterQuery;
};

export const filterStateToFilterGroupState = (
  filterState?: FilterState | FilterGroupState
): FilterGroupState => {
  if (!filterState) return DEFAULT_FILTER_BUILDER_STATE;

  // if it's already a FilterGroupState return it as is
  if (
    isFilterBuilderStateValid(filterState as FilterGroupState) ||
    isDefaultAdvancedFilterState(filterState as FilterGroupState)
  ) {
    return filterState as FilterGroupState;
  }

  const copy = cloneObject(filterState);

  const filterGroupState: FilterGroupState = cloneObject(
    DEFAULT_FILTER_BUILDER_STATE
  );

  // convert FilterState -> FilterGroupState
  Object.keys(copy as FilterState).forEach((fieldName, index) => {
    const newGroupChild: FilterRowState = {
      type: "row",
      id: `row-${index}`,
      attribute: fieldName,
      ...(copy as FilterState)[fieldName],
    };

    // override the first one which is part of the default state
    if (index === 0) {
      filterGroupState.children[0] = newGroupChild;
    } else {
      filterGroupState.children.push(newGroupChild);
    }
  });

  return filterGroupState;
};

export const getSelectOptionsFromFilterGroupState = (
  filters: FilterGroupState | undefined
): { [key: string]: SelectOption[] } => {
  if (!filters) return {};

  return getFilterGroupStateTopLevelRowAttributes(filters).reduce(
    (acc: { [key: string]: SelectOption[] }, attribute) => {
      const values = getTopLevelRowFromFilterGroupState(
        attribute,
        filters
      )?.values;
      if (!values) return acc;

      acc[attribute] = Array.isArray(values)
        ? values.filter((v) => v).map((v) => ({ id: v, value: v }))
        : [{ id: values, value: values }];

      return acc;
    },
    {}
  );
};

/**
 * Merges two filter rows with "any-like" operators (IN, NOT_IN, CONTAINS, etc.)
 * by combining their values into a single unique set.
 * Also converts EQUALS to IN and NOT_EQUALS to NOT_IN for consistency.
 */
const mergeAnyOfOperatorRows = (
  firstRow: FilterRowState,
  secondRow: FilterRowState
) => {
  if (firstRow.operator === FilterOperator.EQUALS) {
    firstRow.operator = FilterOperator.IN;
  } else if (firstRow.operator === FilterOperator.NOT_EQUALS) {
    firstRow.operator = FilterOperator.NOT_IN;
  }

  const uniqueValues = new Set([
    ...(firstRow.values ?? []),
    ...(secondRow.values ?? []),
  ]);

  firstRow.values = Array.from(uniqueValues);

  return firstRow;
};

/**
 * Checks if a string value is a valid date.
 * Uses the native Date constructor to validate.
 */
const isDateString = (value: string): boolean => {
  if (!value) return false;

  // Attempt to create a Date object
  const date = new Date(value);

  // Check if the date is valid
  return isValidDate(date);
};

/**
 * Checks if a string value is numeric (integer or float).
 * Uses Number() constructor and isFinite to validate.
 */
const isNumericString = (value: string): boolean => {
  if (!value) return false;

  // Convert to number and check if it's finite
  const num = Number(value);

  return !isNaN(num) && isFinite(num);
};

/**
 * Compares two values based on their type (date, number, or string).
 * Returns:
 * - negative number if value1 < value2
 * - 0 if value1 === value2
 * - positive number if value1 > value2
 */
const compareValues = (value1: string, value2: string): number => {
  // Check if both values are dates
  const isDate1 = isDateString(value1);
  const isDate2 = isDateString(value2);

  if (isDate1 && isDate2) {
    const date1 = new Date(value1);
    const date2 = new Date(value2);

    return date1.getTime() - date2.getTime();
  }

  // Check if both values are numbers
  const isNum1 = isNumericString(value1);
  const isNum2 = isNumericString(value2);

  if (isNum1 && isNum2) {
    const num1 = Number(value1);
    const num2 = Number(value2);

    return num1 - num2;
  }

  // If types don't match or both are strings, do string comparison
  return value1.localeCompare(value2);
};

const mergeGreaterLessThanOperatorRows = (
  firstRow: FilterRowState,
  secondRow: FilterRowState,
  anyAll: AnyAll
) => {
  const firstValue = firstRow.values?.[0] ?? "";
  const secondValue = secondRow.values?.[0] ?? "";

  // < 0 if firstValue < secondValue
  // 0 if firstValue === secondValue
  // > 0 if firstValue > secondValue
  const comparedValue = compareValues(firstValue, secondValue);

  if (
    firstRow.operator &&
    [FilterOperator.GREATER_OR_EQUAL, FilterOperator.GREATER_THAN].includes(
      firstRow.operator
    )
  ) {
    if (anyAll === "all") {
      if (comparedValue < 0) {
        return secondRow;
      } else if (comparedValue > 0) {
        return firstRow;
      } else {
        if (
          secondRow.operator === FilterOperator.GREATER_THAN ||
          firstRow.operator === FilterOperator.GREATER_THAN
        ) {
          firstRow.operator = FilterOperator.GREATER_THAN;
        }

        return firstRow;
      }
    } else {
      if (comparedValue < 0) {
        return firstRow;
      } else if (comparedValue > 0) {
        return secondRow;
      } else {
        if (secondRow.operator === FilterOperator.GREATER_OR_EQUAL) {
          return secondRow;
        }

        return firstRow;
      }
    }
  } else {
    if (anyAll === "all") {
      if (comparedValue < 0) {
        return firstRow;
      } else if (comparedValue > 0) {
        return secondRow;
      } else {
        if (
          secondRow.operator === FilterOperator.LESS_THAN ||
          firstRow.operator === FilterOperator.LESS_THAN
        ) {
          firstRow.operator = FilterOperator.LESS_THAN;
        }

        return firstRow;
      }
    } else {
      if (comparedValue < 0) {
        return secondRow;
      } else if (comparedValue > 0) {
        return firstRow;
      } else {
        if (secondRow.operator === FilterOperator.LESS_OR_EQUAL) {
          return secondRow;
        }

        return firstRow;
      }
    }
  }
};

const mergeTwoRowsPerOperator = (
  firstRow: FilterRowState,
  secondRow: FilterRowState,
  anyAll: AnyAll
): FilterRowState => {
  switch (firstRow.operator) {
    case FilterOperator.IN:
    case FilterOperator.NOT_IN:
    case FilterOperator.CONTAINS:
    case FilterOperator.NOT_CONTAINS:
    case FilterOperator.STARTS_WITH:
    case FilterOperator.NOT_STARTS_WITH:
      return mergeAnyOfOperatorRows(firstRow, secondRow);
    case FilterOperator.IS_EMPTY:
    case FilterOperator.IS_NOT_EMPTY:
      return firstRow;
    case FilterOperator.IS_TRUE:
    case FilterOperator.IS_FALSE:
      return firstRow;
    case FilterOperator.GREATER_THAN:
    case FilterOperator.GREATER_OR_EQUAL:
      return mergeGreaterLessThanOperatorRows(firstRow, secondRow, anyAll);
    case FilterOperator.LESS_THAN:
    case FilterOperator.LESS_OR_EQUAL:
      return mergeGreaterLessThanOperatorRows(firstRow, secondRow, anyAll);
    default:
      return firstRow;
  }
};

const consolidateRowsByAttributes = (
  rows: FilterRowState[],
  anyAll: AnyAll
): FilterRowState[] => {
  const visitedAttributes: {
    attribute: string;
    index: number;
    operator: string;
    row: FilterRowState;
  }[] = [];

  for (let i = 0; i < rows.length; i++) {
    const { attribute, operator } = rows[i];
    if (!operator || !attribute) continue;

    if (SIMPLIFY_FILTER_IGNORED_OPERATORS.includes(operator)) {
      visitedAttributes.push({
        attribute,
        index: visitedAttributes.length,
        operator,
        row: rows[i],
      });
    }

    const existingRow = visitedAttributes.find(
      (row) =>
        row.attribute === attribute &&
        SIMPLIFY_FILTER_MAPPING[operator].includes(
          row.operator as FilterOperator
        )
    );

    if (attribute && existingRow) {
      visitedAttributes[existingRow.index].row = mergeTwoRowsPerOperator(
        visitedAttributes[existingRow.index].row,
        rows[i],
        anyAll
      );
    } else if (attribute && operator) {
      visitedAttributes.push({
        attribute,
        index: visitedAttributes.length,
        operator,
        row: rows[i],
      });
    }
  }

  return visitedAttributes.map((row) => row.row);
};

const RELATION_SUBSET_OPERATORS: FilterOperator[] = [FilterOperator.EXISTS];

const IN_LAST_UNIT_TO_SUB: Record<
  string,
  (date: Date, amount: number) => Date
> = {
  d: subDays,
  w: subWeeks,
  m: subMonths,
  y: subYears,
};

// Resolves an IN_LAST value (e.g. `12m`) to the window's start date, using the
// same calendar arithmetic the backend applies (`12m` and `1y` land on the same
// day). Returns null for unparseable values. A later start date means a more
// restrictive window.
const parseInLastToStartDate = (value: string, now: Date): Date | null => {
  const match = /^(\d+)(d|w|m|y)$/.exec(value);
  if (!match) return null;

  return IN_LAST_UNIT_TO_SUB[match[2]](now, Number(match[1]));
};

/**
 * Rewrites relative IN_LAST date rows (e.g. `2w`) into absolute
 * GREATER_OR_EQUAL rows anchored at the window's start date, recursing into
 * nested groups and into the nested filter of EXISTS relation rows (whose date
 * row is encoded as a `[...]` string in the row's value). Used when dashboard
 * filters are carried into an investigation, whose date window should stay
 * pinned rather than drift with "now". Returns the input state unchanged (same
 * reference) when there is nothing to convert, so callers can cheaply detect a
 * no-op.
 */
export const convertInLastToAfterDateFilters = (
  filterGroupState: FilterGroupState
): FilterGroupState => {
  const children = filterGroupState.children.map((child) => {
    if (child.type === "group") return convertInLastToAfterDateFilters(child);

    if (isRelationSubsetRow(child)) {
      return convertInLastInRelationSubsetRow(child);
    }

    if (child.operator !== FilterOperator.IN_LAST || !child.values?.length) {
      return child;
    }

    const startDate = parseInLastToStartDate(child.values[0], new Date());
    if (!startDate) return child;

    return {
      ...child,
      operator: FilterOperator.GREATER_OR_EQUAL,
      values: [startDate.toISOString().split("T")[0]],
    };
  });

  const changed = children.some(
    (child, i) => child !== filterGroupState.children[i]
  );

  return changed ? { ...filterGroupState, children } : filterGroupState;
};

// Applies the IN_LAST-to-absolute conversion to the nested filter of an EXISTS
// relation row (encoded as a `[...]` string in the row's single value). Returns
// the row unchanged when the nested filter can't be parsed, serializes to
// nothing, or has no IN_LAST row to convert.
const convertInLastInRelationSubsetRow = (
  row: FilterRowState & { values: [string] }
): FilterRowState => {
  const nested = parseNestedFilterValue(row.values[0]);
  if (!nested) return row;

  const converted = convertInLastToAfterDateFilters(nested);
  if (converted === nested) return row;

  const nestedQuery = getFiltersQuery(converted);
  if (!nestedQuery) return row;

  return { ...row, values: [`[${nestedQuery}]`] };
};

const isRelationSubsetRow = (
  row: FilterRowState
): row is FilterRowState & { values: [string]; operator: FilterOperator } =>
  !!row.operator &&
  RELATION_SUBSET_OPERATORS.includes(row.operator) &&
  row.values?.length === 1 &&
  row.values[0].startsWith("[") &&
  row.values[0].endsWith("]");

const parseNestedFilterValue = (value: string): FilterGroupState | undefined =>
  filterBuilderQueryToFilterBuilderState(value.slice(1, -1));

const stripIds = (
  node: FilterGroupState | FilterRowState
): FilterGroupState | FilterRowState =>
  node.type === "group"
    ? { ...node, id: "", children: node.children.map(stripIds) }
    : { ...node, id: "" };

const nodesEqualIgnoringId = (
  a: FilterGroupState | FilterRowState,
  b: FilterGroupState | FilterRowState
): boolean => JSON.stringify(stripIds(a)) === JSON.stringify(stripIds(b));

/**
 * When `a` and `b` are identical except for a single IN_LAST row on the same
 * attribute, returns the two windows' start dates (relative to the same `now`),
 * else null. Comparing resolved dates rather than a fixed day-count keeps units
 * consistent with the backend — e.g. `12m` and `1y` resolve to the same day, so
 * they consolidate rather than being mistaken for different windows.
 */
const getInLastWindowDiff = (
  a: FilterGroupState,
  b: FilterGroupState
): { aStart: Date; bStart: Date } | null => {
  if (a.anyAll !== b.anyAll || a.children.length !== b.children.length) {
    return null;
  }

  let diffIndex = -1;
  for (let i = 0; i < a.children.length; i++) {
    if (!nodesEqualIgnoringId(a.children[i], b.children[i])) {
      if (diffIndex !== -1) return null;

      diffIndex = i;
    }
  }

  if (diffIndex === -1) return null;

  const aRow = a.children[diffIndex];
  const bRow = b.children[diffIndex];

  if (
    aRow.type !== "row" ||
    bRow.type !== "row" ||
    aRow.operator !== FilterOperator.IN_LAST ||
    bRow.operator !== FilterOperator.IN_LAST ||
    aRow.attribute !== bRow.attribute
  ) {
    return null;
  }

  const now = new Date();
  const aStart = parseInLastToStartDate(aRow.values?.[0] ?? "", now);
  const bStart = parseInLastToStartDate(bRow.values?.[0] ?? "", now);
  if (!aStart || !bStart) return null;

  return { aStart, bStart };
};

/**
 * Consolidates sibling relation rows (EXISTS/OCCURS/MATCH with a nested filter
 * value) that are identical except for a single IN_LAST window, keeping only the
 * more restrictive one. In an "all" group the smaller window is the subset; in an
 * "any" group the larger window is the superset. Exact duplicates are deduped.
 */
const consolidateSubsetRelationRows = (
  rows: FilterRowState[],
  anyAll: AnyAll
): FilterRowState[] => {
  const result: FilterRowState[] = [];

  for (const row of rows) {
    if (!isRelationSubsetRow(row)) {
      result.push(row);
      continue;
    }

    const nested = parseNestedFilterValue(row.values[0]);
    if (!nested) {
      result.push(row);
      continue;
    }

    let merged = false;
    for (let i = 0; i < result.length; i++) {
      const existing = result[i];
      if (
        !isRelationSubsetRow(existing) ||
        existing.attribute !== row.attribute ||
        existing.operator !== row.operator
      ) {
        continue;
      }

      if (existing.values[0] === row.values[0]) {
        merged = true;
        break;
      }

      const existingNested = parseNestedFilterValue(existing.values[0]);
      if (!existingNested) continue;

      const diff = getInLastWindowDiff(existingNested, nested);
      if (!diff) continue;

      // A later start date is the more restrictive window: keep it in an "all"
      // group and the earlier (wider) one in an "any" group.
      const keepNew =
        anyAll === "all"
          ? diff.bStart > diff.aStart
          : diff.bStart < diff.aStart;
      if (keepNew) result[i] = row;

      merged = true;
      break;
    }

    if (!merged) result.push(row);
  }

  return result;
};

// This function flattens unnecessary groups in the filter group state if
// they are of the same anyAll type as the parent group.
const flattenUnnecessaryGroups = (
  parentAnyAll: AnyAll,
  children: FilterGroupState["children"]
): FilterGroupState["children"] => {
  let newChildren = [];

  for (const child of children) {
    if (child.type === "group" && child.anyAll === parentAnyAll) {
      newChildren.push(...child.children);
    } else {
      newChildren.push(child);
    }
  }

  return newChildren;
};

const simplifyFilterGroupStateInner = (
  filterGroupState: FilterGroupState
): FilterGroupState | FilterRowState => {
  if (filterGroupState.children.length === 1) {
    const child = filterGroupState.children[0];

    if (child.type === "group") {
      return simplifyFilterGroupStateInner(child);
    } else {
      return child;
    }
  }

  for (let i = 0; i < filterGroupState.children.length; i++) {
    const child = filterGroupState.children[i];
    if (child.type === "group") {
      filterGroupState.children[i] = simplifyFilterGroupStateInner(child);
    }
  }

  const flattened = flattenUnnecessaryGroups(
    filterGroupState.anyAll,
    filterGroupState.children
  );

  const consolidated = consolidateSubsetRelationRows(
    consolidateRowsByAttributes(
      flattened.filter((child) => child.type === "row") as FilterRowState[],
      filterGroupState.anyAll
    ),
    filterGroupState.anyAll
  );

  filterGroupState.children = [
    ...flattened.filter((c) => c.type === "group"),
    ...consolidated,
  ];

  return filterGroupState;
};

export const simplifyFilterGroupState = (
  filterGroupStateParam: FilterGroupState | undefined
): FilterGroupState => {
  if (!filterGroupStateParam) return DEFAULT_FILTER_BUILDER_STATE;

  const filterGroupState = cloneObject(filterGroupStateParam);

  if (!filterGroupState.children.length) return DEFAULT_FILTER_BUILDER_STATE;

  const newChildren = [];

  for (const child of filterGroupState.children) {
    if (child.type === "group") {
      newChildren.push(simplifyFilterGroupStateInner(child));
    } else {
      newChildren.push(child);
    }
  }

  const flattened = flattenUnnecessaryGroups(
    filterGroupState.anyAll,
    newChildren
  );

  const consolidated = consolidateSubsetRelationRows(
    consolidateRowsByAttributes(
      flattened.filter((child) => child.type === "row") as FilterRowState[],
      filterGroupState.anyAll
    ),
    filterGroupState.anyAll
  );

  filterGroupState.children = [
    ...flattened.filter((c) => c.type === "group"),
    ...consolidated,
  ];

  if (filterGroupState.children.length === 1) {
    filterGroupState.anyAll = "all";
  }

  return filterGroupState;
};

const filterRowDedupeKey = (row: FilterRowState): string =>
  JSON.stringify([row.attribute, row.operator, row.values]);

/**
 * Fake "merge" arbitrary number of FilterGroupState objects by concatenating their children.
 * - It ignores invalid filter states
 * - makes sure IDs are unique
 * - if any of the filters has "any" as anyAll, it leave it in a separate group within top-level "all" group
 * - drops exact-duplicate rows (same attribute, operator, values) so merging a filter with itself yields one row
 */
export const mergeFilterGroupStates = (
  ...states: (FilterGroupState | undefined)[]
): FilterGroupState => {
  // Filter out undefined states & states with invalid children (ie. DEFAULT_FILTER_BUILDER_STATE)
  const validStates = states
    .filter((state): state is FilterGroupState => state !== undefined)
    .filter((state) => state && isFilterBuilderStateValid(state));

  if (validStates.length === 0) {
    return DEFAULT_FILTER_BUILDER_STATE;
  }

  // Start with a top-level "all" group
  const mergedState: FilterGroupState = {
    id: `group-${randomID()}`,
    type: "group",
    anyAll: "all",
    children: [],
  };

  // Iterate over the valid states and merge them
  for (const state of validStates) {
    if (state.anyAll === "any") {
      // If the state has "any" as anyAll, create a separate group
      mergedState.children.push({
        ...state,
        id: `group-${randomID()}`,
        children: state.children.map((child) => ({
          ...child,
          id: `${child.type}-${randomID()}`,
        })),
      });
    } else {
      // Otherwise, concatenate the children directly
      mergedState.children = mergedState.children.concat(
        state.children.map((child) => ({
          ...child,
          id: `${child.type}-${randomID()}`,
        }))
      );
    }
  }

  // drop exact-duplicate rows so merging identical filters yields a single row.
  const seenRowKeys = new Set<string>();
  mergedState.children = mergedState.children.filter((child) => {
    if (child.type !== "row") return true;

    const key = filterRowDedupeKey(child);
    if (seenRowKeys.has(key)) return false;

    seenRowKeys.add(key);

    return true;
  });

  // If there's only one child and it's a group, return that group instead
  if (
    mergedState.children.length === 1 &&
    mergedState.children[0].type === "group"
  ) {
    return mergedState.children[0] as FilterGroupState;
  }

  return mergedState;
};

export const mergeFilterQueryStrings = (
  ...filterQueryStrings: (string | undefined)[]
): string => {
  const filterGroupStates = filterQueryStrings
    .filter(Boolean)
    .map((filterQueryString) =>
      filterBuilderQueryToFilterBuilderState(filterQueryString)
    );

  const combinedFilterState = mergeFilterGroupStates(...filterGroupStates);

  return getFiltersQuery(combinedFilterState);
};

export const getSearchFilterQuery = (
  fields: string[],
  searchTerm: string
): string => {
  const term = searchTerm.trim();

  if (!term) return "";

  return getFiltersQuery({
    type: "group",
    id: "search",
    anyAll: "any",
    children: fields.map((field) => ({
      type: "row",
      id: field,
      attribute: field,
      operator: FilterOperator.CONTAINS,
      values: [term],
    })),
  });
};

export const getFilterMenuWrapperClass = (isAdvancedFilter: boolean) =>
  classNames({
    [QUICK_FILTERS_WIDTH_CLASS]: !isAdvancedFilter,
    [ADVANCED_FILTERS_WIDTH_CLASS]: isAdvancedFilter,
  });

/**
 * Transform the old FilterState to new FilterGroupState
 * Old: { fieldName: { values: [value1, value2], operator: "in" }}
 * New: see DEFAULT_FILTER_BUILDER_STATE
 */
export const oldFilterStateToNew = (
  filterState?: FilterState | FilterGroupState
): FilterGroupState | undefined => {
  if (!filterState) return undefined;

  if ("type" in filterState && filterState.type === "group") {
    if (isFilterBuilderStateValid(filterState as FilterGroupState)) {
      return filterState as FilterGroupState;
    }
  }

  if (isFilterStateCorrect(filterState)) {
    return filterStateToFilterGroupState(filterState);
  }

  return filterState;
};
